import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import axiosClient from "../../../api/axiosClient";

const AUTOSAVE_TIMEOUT_MS = 8000;

const getNotificationPrefsFromUser = (user) => ({
	eventsInApp: Boolean(
		user?.notificationPreferences?.events?.inApp ??
			(user?.notifyOnPublish || user?.notifyOnAttend),
	),
	recommendationsInApp: Boolean(
		user?.notificationPreferences?.recommendations?.inApp ??
			(user?.receiveEventSuggestions || user?.notifyOnNearbyEvent),
	),
	profileInApp: Boolean(
		user?.notificationPreferences?.profile?.inApp ??
			(user?.notifyOnFollow || user?.notifyOnVehicleReaction),
	),
	organizerInApp:
		user?.notificationPreferences?.organizerEvents?.inApp ??
		user?.notifyOnOrganizerEventsInApp ??
		true,
	organizerEmail:
		user?.notificationPreferences?.organizerEvents?.email ??
		user?.notifyOnOrganizerEventsEmail ??
		true,
});

function useNotificationPreferences({ user, setUser, t }) {
	const [prefs, setPrefs] = useState(() => getNotificationPrefsFromUser(user));
	const [savingOptionKeys, setSavingOptionKeys] = useState([]);
	const [initializedPrefs, setInitializedPrefs] = useState(false);
	const prefsRef = useRef(getNotificationPrefsFromUser(user));

	useEffect(() => {
		if (!user) {
			return;
		}

		if (!initializedPrefs) {
			const nextPrefs = getNotificationPrefsFromUser(user);
			setPrefs(nextPrefs);
			prefsRef.current = nextPrefs;
			setInitializedPrefs(true);
			return;
		}

		if (savingOptionKeys.length === 0) {
			const nextPrefs = getNotificationPrefsFromUser(user);
			setPrefs(nextPrefs);
			prefsRef.current = nextPrefs;
		}
	}, [initializedPrefs, savingOptionKeys.length, user]);

	const autosaveRequestConfig = useMemo(
		() => ({ timeout: AUTOSAVE_TIMEOUT_MS }),
		[],
	);

	const savePreferencesSection = useCallback(
		async (section, optionKey, patch, previousPrefs) => {
			try {
				setSavingOptionKeys((current) => [...current, optionKey]);
				const requests = [];

				if (section === "events" && patch.eventsInApp !== undefined) {
					requests.push(
						axiosClient.put(
							"/api/users/notification-preferences",
							{
								category: "events",
								channels: { inApp: patch.eventsInApp },
							},
							autosaveRequestConfig,
						),
					);
				}

				if (section === "profile" && patch.profileInApp !== undefined) {
					requests.push(
						axiosClient.put(
							"/api/users/notification-preferences",
							{
								category: "profile",
								channels: { inApp: patch.profileInApp },
							},
							autosaveRequestConfig,
						),
					);
				}

				if (section === "organizerEvents") {
					const channels = {};
					if (patch.organizerInApp !== undefined) {
						channels.inApp = patch.organizerInApp;
					}
					if (patch.organizerEmail !== undefined) {
						channels.email = patch.organizerEmail;
					}
					if (Object.keys(channels).length > 0) {
						requests.push(
							axiosClient.put(
								"/api/users/notification-preferences",
								{
									category: "organizerEvents",
									channels,
								},
								autosaveRequestConfig,
							),
						);
					}
				}

				if (
					section === "recommendations" &&
					patch.recommendationsInApp !== undefined
				) {
					requests.push(
						axiosClient.put(
							"/api/users/preferences",
							{
								receiveEventSuggestions: patch.recommendationsInApp,
								notifyOnNearbyEvent: patch.recommendationsInApp,
							},
							autosaveRequestConfig,
						),
					);
				}

				const results = await Promise.all(requests);
				const lastUser =
					results[results.length - 1]?.data?.user || results[0]?.data?.user;
				if (lastUser) {
					setUser((currentUser) => {
						if (!currentUser) {
							return currentUser;
						}

						const nextUser = {
							...currentUser,
							notificationPreferences: {
								...(currentUser.notificationPreferences || {}),
								...(lastUser.notificationPreferences || {}),
							},
						};

						if (section === "events") {
							nextUser.notifyOnPublish = lastUser.notifyOnPublish;
							nextUser.notifyOnAttend = lastUser.notifyOnAttend;
						}

						if (section === "profile") {
							nextUser.notifyOnFollow = lastUser.notifyOnFollow;
							nextUser.notifyOnVehicleReaction =
								lastUser.notifyOnVehicleReaction;
						}

						if (section === "organizerEvents") {
							if (patch.organizerInApp !== undefined) {
								nextUser.notifyOnOrganizerEventsInApp =
									lastUser.notifyOnOrganizerEventsInApp;
							}
							if (patch.organizerEmail !== undefined) {
								nextUser.notifyOnOrganizerEventsEmail =
									lastUser.notifyOnOrganizerEventsEmail;
							}
						}

						if (section === "recommendations") {
							nextUser.receiveEventSuggestions =
								lastUser.receiveEventSuggestions;
							nextUser.notifyOnNearbyEvent = lastUser.notifyOnNearbyEvent;
							nextUser.nearbyRadiusKm = lastUser.nearbyRadiusKm;
						}

						return nextUser;
					});
				}
				toast.success(t("settings.notifications.saveSuccess"));
			} catch {
				setPrefs((currentPrefs) => {
					const revertedPrefs = {
						...currentPrefs,
						...Object.fromEntries(
							Object.keys(patch).map((key) => [key, previousPrefs[key]]),
						),
					};
					prefsRef.current = revertedPrefs;
					return revertedPrefs;
				});
				toast.error(t("settings.notifications.saveError"));
			} finally {
				setSavingOptionKeys((current) =>
					current.filter((currentOptionKey) => currentOptionKey !== optionKey),
				);
			}
		},
		[autosaveRequestConfig, setUser, t],
	);

	const handleSectionAutosave = useCallback(
		(section, optionKey, patch) => {
			const previousPrefs = { ...prefsRef.current };
			const nextPrefs = { ...prefsRef.current, ...patch };
			prefsRef.current = nextPrefs;
			setPrefs(nextPrefs);
			void savePreferencesSection(section, optionKey, patch, previousPrefs);
		},
		[savePreferencesSection],
	);

	return {
		prefs,
		savingOptionKeys,
		handleSectionAutosave,
	};
}

export { getNotificationPrefsFromUser };
export default useNotificationPreferences;
